import type { CadFeatureMap } from '@mark43/cad-api';

/**
 * The feature flags worth having on a black-map episode: the ones that change how the
 * map is mounted, recovered, rendered or diagnosed.
 *
 * Episodes used to carry the whole feature map — ~250 flags, 74% of every episode
 * event — of which the dashboard read one. Add a flag here when it affects the map.
 * Typed against `CadFeatureMap`, so a renamed or removed flag fails the build.
 */
export const MAP_RELEVANT_FLAGS: ReadonlyArray<keyof CadFeatureMap> = [
    // Black-map fixes and recovery
    'mapStayMountedOnReconnectEnabled',
    'mapDebounceMapReconnectsEnabled',
    'mapReleaseWebglContextOnTeardownEnabled',
    'enableInfiniteMapRecoveryEnabled',
    // How the map is mounted and fed
    'noBackgroundedMapEnabled',
    'mapTileCacheEnabled',
    'firstResponderMapContainerFixedPositionEnabled',
    'mapViewStreamEnabled',
    'useMapboxMapForLocationInputs',
    // What the map has to render
    'enableUnitClusteringOnMap',
    'esriFeatureReductionClusteringEnabled',
    'showMobileDevicesOnMap',
    'showMobileDevicesPolylinesOnMap',
    // Credentials and diagnostics
    'esriOauthTokenEnabled',
    'diagnosticContextEnabled',
];

export type MapRelevantFlags = Partial<Record<keyof CadFeatureMap, boolean>>;

export function pickMapRelevantFlags(enabledFeatures: CadFeatureMap): MapRelevantFlags {
    const picked: MapRelevantFlags = {};
    for (const flag of MAP_RELEVANT_FLAGS) {
        picked[flag] = enabledFeatures[flag];
    }
    return picked;
}
